/**
 * Subject choices for pickers, sourced from the Cosmos catalogue.
 *
 * The catalogue is the source of truth for which subjects exist. Pickers used
 * to build their options from the `SUBJECT_OPTIONS` code constant instead,
 * which let the two drift: a student could select a subject that had no
 * catalogue row, and every surface that renders from the catalogue then
 * dropped it silently. That is exactly what happened to biology on prod —
 * 1,086 students held it, 1,995 questions existed, and Practice showed
 * nothing, because the row was missing.
 *
 * `SUBJECT_OPTIONS` stays as a hard allowlist, not as the authority. Anything
 * stored on a user must satisfy `SubjectSchema` server-side, so offering a
 * catalogue subject outside that enum would produce a picker whose selection
 * fails to save. Catalogue entries outside the enum are therefore skipped and
 * warned about — visible rather than silent, and the fix is a code change to
 * add the subject to the enum.
 *
 * The code list is also the cold-start fallback: if the catalogue has not
 * loaded (first run, offline, failed fetch), an empty picker would block
 * registration outright, which is worse than briefly offering the code list.
 */
import { useMemo } from 'react';
import { SUBJECT_OPTIONS, SUBJECT_NAMES } from '@padcanvas/shared';
import { useSubjects } from './useSubjects';

export interface SubjectFormOption {
    value: string;
    label: string;
}

/** Shape actually stored in the catalogue (superset of SubjectBase). */
interface CatalogueSubject {
    id: string;
    name: string;
    displayName: string;
    isActive?: boolean;
    sortOrder?: number;
}

const STORABLE = new Set<string>(SUBJECT_OPTIONS as readonly string[]);

const CODE_FALLBACK: SubjectFormOption[] = SUBJECT_OPTIONS.map((v) => ({
    value: v,
    label: SUBJECT_NAMES[v] ?? v,
}));

/** Catalogue subjects a user may pick, ordered as the catalogue orders them. */
export function useSubjectFormOptions(): SubjectFormOption[] {
    const { subjects } = useSubjects<CatalogueSubject>();

    return useMemo(() => {
        const active = (subjects ?? []).filter((s) => s.isActive !== false);
        if (active.length === 0) return CODE_FALLBACK;

        const unstorable = active.filter((s) => !STORABLE.has(s.name.toLowerCase()));
        if (unstorable.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
                '[subjects] catalogue entries have no SubjectSchema value and cannot be saved — '
                + 'add them to SUBJECT_OPTIONS in shared/src/academicTypes.ts: '
                + unstorable.map((s) => s.name).join(', '),
            );
        }

        const options = active
            .filter((s) => STORABLE.has(s.name.toLowerCase()))
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
            .map((s) => ({
                value: s.name.toLowerCase(),
                label: s.displayName || SUBJECT_NAMES[s.name.toLowerCase()] || s.name,
            }));

        return options.length > 0 ? options : CODE_FALLBACK;
    }, [subjects]);
}
